import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import ImageUpload from "../../components/ImageUpload";

const EMPTY = { name: "", slug: "", image: "", description: "" };

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/categories").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || "", slug: c.slug || "", image: c.image || "", description: c.description || "" });
    setOpen(true);
  };

  const save = async () => {
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    if (!payload.name || !payload.slug) {
      toast.error("Name and slug are required");
      return;
    }
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/admin/categories/${id}`);
    toast.success("Category deleted");
    load();
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Categories</h1>
          <p className="text-ink-soft text-sm mt-1">{rows.length} categories</p>
        </div>
        <button onClick={openNew} className="btn-ink flex items-center gap-2 px-5 py-3 overline" data-testid="add-category-btn">
          <Plus size={15} /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((c) => (
          <div key={c.id} className="border border-line" data-testid={`admin-cat-${c.slug}`}>
            <div className="aspect-[4/3] bg-[#efece6] overflow-hidden">
              {c.image ? <img src={c.image} alt={c.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-serif text-xl">{c.name}</h3>
                  <p className="mono text-[0.65rem] text-ink-soft mt-1">/{c.slug}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(c)} className="p-2 hover:text-gold-deep" data-testid={`edit-cat-${c.slug}`}><Pencil size={14} /></button>
                  <button onClick={() => del(c.id)} className="p-2 hover:text-red-700" data-testid={`del-cat-${c.slug}`}><Trash2 size={14} /></button>
                </div>
              </div>
              {c.description && <p className="text-xs text-ink-soft mt-2 line-clamp-2">{c.description}</p>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <L label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: editing ? form.slug : slugify(e.target.value) })} data-testid="cat-name" /></L>
            <L label="Slug (URL)"><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="cat-slug" /></L>
            <ImageUpload label="Category Image" value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
            <L label="Description (optional)"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></L>
            <button onClick={save} className="btn-ink w-full py-3.5 overline" data-testid="save-cat-btn">{editing ? "Save changes" : "Create category"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><span className="overline text-ink-soft mb-1.5 block">{label}</span>{children}</label>;
}
